/**
 * Data Room Domain Store
 *
 * Manages documents, folder structure, upload state, and
 * the document checklist for startup data rooms.
 */

import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export interface DocumentComment {
  id: string;
  investorId: string;
  investorName: string;
  text: string;
  date: string;
  isPublic: boolean;
  /** Version of the document at the time the comment was written. Defaults to 1
   *  for legacy data without versioning info. Used to flag stale comments when
   *  the document is updated. */
  commentedOnVersion?: number;
}

/** A historical version of a connected/uploaded document. */
export interface DocumentVersion {
  v: number;
  uploadedAt: string;
  size?: string;
  /** Source-provider modifiedTime when the version was detected (Drive/Dropbox). */
  modifiedAt?: string;
}

export type DocumentClassificationConfidence = 'high' | 'medium' | 'low';

export interface DataRoomDocument {
  id: string;
  name: string;
  type: 'pitch_deck' | 'financials' | 'legal' | 'product' | 'team' | 'market_research' | 'other';
  uploadDate: string;
  size: string;
  status: 'pending' | 'reviewed' | 'flagged';
  sharedWith: string[];
  comments: DocumentComment[];
  /** Stable ID from the connected provider (Google Drive fileId, Dropbox path_lower, etc.).
   *  When set, sync logic merges new fetches into the existing record instead of duplicating. */
  providerFileId?: string;
  /** 1-indexed current version. Bumped when the source file's content changes. */
  currentVersion?: number;
  /** Append-only history. Index 0 = v1. */
  versions?: DocumentVersion[];
  /** Optional confidence of the auto-classification. Absent when set manually. */
  classificationConfidence?: DocumentClassificationConfidence;
  /** True if the user has manually overridden the auto-classification. Skip re-classification on sync. */
  typeOverridden?: boolean;
  /** Checklist doc id (from DATA_ROOM_DOCUMENTS) this file covers, when a confident
   *  filename match is detected during sync. The dataroom screen uses this to
   *  auto-mark the corresponding checklist item as 'uploaded'. */
  linkedChecklistDocId?: string;
}

export type RequiredDocumentType = 'pitch_deck' | 'financials' | 'legal' | 'product' | 'team' | 'market_research';

export const REQUIRED_DOCUMENTS: { type: RequiredDocumentType; label: string; priority: 'critical' | 'important' | 'recommended' }[] = [
  { type: 'pitch_deck', label: 'Pitch Deck', priority: 'critical' },
  { type: 'financials', label: 'Financial Projections', priority: 'critical' },
  { type: 'legal', label: 'Cap Table / Legal', priority: 'critical' },
  { type: 'team', label: 'Team Profiles', priority: 'important' },
  { type: 'product', label: 'Product Roadmap', priority: 'important' },
  { type: 'market_research', label: 'Market Analysis', priority: 'recommended' },
];

// ============================================================================
// Store Interface
// ============================================================================

interface DataroomState {
  dataRoomDocuments: DataRoomDocument[];
  setDataRoomDocuments: (docs: DataRoomDocument[]) => void;
  addDocument: (doc: DataRoomDocument) => void;
  /** Manual override of auto-classification. Marks the doc so future syncs don't change it. */
  setDocumentType: (docId: string, type: DataRoomDocument['type']) => void;
  /** Replace fields on a document by id (shallow merge). */
  updateDocument: (docId: string, updates: Partial<DataRoomDocument>) => void;
  /** Append a new version entry and bump currentVersion. */
  bumpDocumentVersion: (docId: string, version: DocumentVersion) => void;

  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  dataRoomDocuments: [] as DataRoomDocument[],
};

// ============================================================================
// Store
// ============================================================================

export const useDataroomStore = create<DataroomState>()(
  (set, get) => ({
    ...initialState,

    setDataRoomDocuments: (docs) => set({ dataRoomDocuments: docs }),
    addDocument: (doc) => set({ dataRoomDocuments: [...get().dataRoomDocuments, doc] }),
    setDocumentType: (docId, type) =>
      set({
        dataRoomDocuments: get().dataRoomDocuments.map((d) =>
          d.id === docId
            ? { ...d, type, typeOverridden: true, classificationConfidence: undefined }
            : d,
        ),
      }),
    updateDocument: (docId, updates) =>
      set({
        dataRoomDocuments: get().dataRoomDocuments.map((d) =>
          d.id === docId ? { ...d, ...updates } : d,
        ),
      }),
    bumpDocumentVersion: (docId, version) =>
      set({
        dataRoomDocuments: get().dataRoomDocuments.map((d) => {
          if (d.id !== docId) return d;
          const versions = [...(d.versions ?? [{ v: 1, uploadedAt: d.uploadDate, size: d.size }]), version];
          return { ...d, versions, currentVersion: version.v };
        }),
      }),

    reset: () => set(initialState),
  })
);
